import React from 'react';
import ReactDOM from 'react-dom';
import { BrowserRouter as Router } from 'react-router-dom';
import mixpanel from 'mixpanel-browser';
import { initDataDog } from '@sunbit-dev/common/services/datadog';
import httpClient from '@sunbit-dev/common/services/httpClient';
import { initConfig } from '@sunbit-dev/common/services/config';
import { asyncWithLDProvider } from 'launchdarkly-react-client-sdk';
import App from './containers/App';
import sunbitApi from './services/SunbitApi';
import { AppContextProvider } from './contexts/appContext';
import { ABTestsContextProvider } from './contexts/abTests/abTestsContext';
import { initFingerprint } from './helpers/fingerprint';

import { initI18n } from '@sunbit-dev/translations';
import { LogsTracking } from '~/components/logsTracking/LogsTracking';
import { getTranslationResources } from './services/translations/Translations';

(async () => {
  const configService = await initConfig(window.sunbitEnv, '/config', window.sunbitE2eEnvNamespace);

  httpClient.setBaseUrl(configService.getItem('apiUrl'));
  sunbitApi.init(httpClient);

  const launchDarklyConfig = configService.getItem('launchDarkly');
  const LDProviderPromise = asyncWithLDProvider({
    clientSideID: launchDarklyConfig?.id,
    reactOptions: {
      useCamelCaseFlagKeys: true,
    },
    options: { disableSyncEventPost: true },
  });

  const translationsPromise = initI18n({
    debug: true,
    getResources: getTranslationResources,
  });

  const promises = [LDProviderPromise, translationsPromise];

  initFingerprint(configService);

  if (window.sunbitEnv !== 'automation') {
    try {
      initDataDog({
        applicationId: '5061a6f9-1f6f-4688-a177-8476e2d6ef01',
        clientToken: 'pub8ccb109901f629e966e79b296f5d2ca3',
        env: window.sunbitEnv,
        service: 'apply-online',
        version: '1.0.0',
        sampleRate: 100,
        trackInteractions: true,
        proxy: configService.getItem('datadogProxy'),
      });
    } catch {
      console.log('Error loading Analytics tools');
    }
  }

  // TODO - move it into the analytics if statement after all track events are conditional too, we need to export all events to a helper function for this
  const mixpanelHost = window.sunbitEnv === 'oregon-prod' ? 'https://mixpanel-proxy.sunbit.com' : 'https://mixpanel-proxy.sunbit.in';
  mixpanel.init(configService.getItem('mixpanelToken'), {
    api_host: mixpanelHost,
    opt_out_tracking_by_default: configService.getItem('mixpanelOptOutTracking'),
  });

  await Promise.all(promises);
  const LDProvider = await LDProviderPromise;

  ReactDOM.render(
    <Router>
      <LDProvider>
        <AppContextProvider>
          <ABTestsContextProvider>
            <App />
          </ABTestsContextProvider>
        </AppContextProvider>
      </LDProvider>
    </Router>,
    document.getElementById('root')
  );
})();
